<template>
  <div class="counsel-panel">
    <a-alert
      type="info"
      show-icon
      class="intro-alert"
      message="상담·고객 이관은 counsel-api 가 서버측에서 파싱합니다"
      description="업로드한 data-store.json 을 그대로 counsel-api 로 보내 EndUser / 상담방 / 메시지 계획을 서버가 계산합니다. 드라이런은 적재하지 않고 요약만 돌려주며, 적재는 멱등이라 여러 번 실행해도 서버가 중복을 건너뜁니다."
    />

    <!-- 병원은 상단 공용 선택기에서 고른다. 아직 안 골랐으면 안내만 한다. -->
    <a-alert
      v-if="!companyId"
      type="warning"
      show-icon
      class="need-company"
      message="대상 병원을 먼저 선택하세요"
      description="상단의 대상 병원 선택기에서 병원을 고르면 이 파일의 상담·고객 데이터를 해당 병원으로 이관합니다."
    />

    <!-- 탐지된 규모 (data-store 키 접두어 기준, 상한 근사값) -->
    <div v-if="detected" class="detected-line">
      <span class="muted">이 파일에서 발견된 상담 대상:</span>
      <a-tag color="blue">고객 {{ detected.patients.toLocaleString() }}</a-tag>
      <a-tag color="blue">상담방 {{ detected.rooms.toLocaleString() }}</a-tag>
      <a-tag color="blue">메시지 {{ detected.messages.toLocaleString() }}</a-tag>
    </div>

    <div class="action-row">
      <a-button
        type="primary"
        class="yellow-button"
        :loading="previewing"
        :disabled="!companyId || !file"
        @click="runPreview"
      >
        <template #icon><ExperimentOutlined /></template>
        서버 드라이런 실행
      </a-button>
      <span v-if="previewResult && !progress" class="muted">
        드라이런 완료 · 실제 적재 전 요약입니다.
      </span>
    </div>

    <!-- ===== 청크 진행률 (브라우저에서 파싱해 고객 단위로 나눠 올린다) ===== -->
    <div v-if="progress" class="chunk-progress">
      <a-progress
        :percent="Math.round((progress.current / progress.total) * 100)"
        :status="progress.current < progress.total ? 'active' : 'success'"
      />
      <span class="muted">
        청크 {{ progress.current }}/{{ progress.total }} 처리 중 · 90MB 를 브라우저에서 나눠
        올리므로 서버에 파일 전체가 올라가지 않습니다.
      </span>
    </div>

    <!-- ===== 드라이런 요약 ===== -->
    <template v-if="previewResult">
      <div class="stats-cards">
        <a-row :gutter="16">
          <a-col :span="8">
            <a-card>
              <a-statistic title="EndUser" :value="previewResult.summary.endUsers" suffix="명" />
              <div class="stat-sub muted">기존 {{ previewResult.summary.endUsersExisting }}명 포함</div>
            </a-card>
          </a-col>
          <a-col :span="8">
            <a-card>
              <a-statistic title="ChatRoom" :value="previewResult.summary.rooms" suffix="개" />
              <div class="stat-sub muted">스킵 {{ previewResult.summary.roomsSkipped }}개</div>
            </a-card>
          </a-col>
          <a-col :span="8">
            <a-card>
              <a-statistic title="Message" :value="previewResult.summary.messages" suffix="건" />
              <div class="stat-sub muted">스킵 {{ previewResult.summary.messagesSkipped }}건</div>
            </a-card>
          </a-col>
        </a-row>
        <a-row :gutter="16" style="margin-top: 16px">
          <a-col :span="8">
            <a-card>
              <a-statistic title="태그 보유 고객" :value="previewResult.summary.tagged" suffix="명" />
              <div class="stat-sub muted">
                상담방 없이 CDP 만: {{ previewResult.summary.orphanCustomers }}명
                <template v-if="previewResult.summary.orphanCustomersUnreachable">
                  / 전화 없어 제외 {{ previewResult.summary.orphanCustomersUnreachable }}명
                </template>
              </div>
            </a-card>
          </a-col>
          <a-col :span="8">
            <a-card>
              <a-statistic
                title="첨부 (살아있음)"
                :value="previewResult.summary.attachmentsRecoverable"
                :value-style="{ color: '#52c41a' }"
                suffix="건"
              />
            </a-card>
          </a-col>
          <a-col :span="8">
            <a-card>
              <a-statistic
                title="첨부 (만료)"
                :value="previewResult.summary.attachmentsExpired"
                :value-style="{ color: '#8c8c8c' }"
                suffix="건"
              />
            </a-card>
          </a-col>
        </a-row>
      </div>

      <a-alert
        type="info"
        show-icon
        class="attach-note"
        message="첨부는 지금 복구하지 않습니다"
        description="원본 CDN(카카오/네이버/메타)은 대부분 TTL 이 지나 죽어 있습니다. 적재는 URL 과 만료 여부만 기록하고, 복구는 서버 측 후속 작업으로 미룹니다."
      />

      <!-- 제외된 방 -->
      <template v-if="excludedRows.length">
        <div class="section-title">제외된 방</div>
        <a-table
          :columns="excludedColumns"
          :data-source="excludedRows"
          :pagination="false"
          :row-key="(record) => record.reason"
          size="small"
          class="excluded-table"
        />
      </template>

      <!-- 샘플 미리보기 (서버가 내려준 경우) -->
      <template v-if="sampleRows.length">
        <div class="section-title">샘플 (상위 {{ sampleRows.length }}건)</div>
        <a-table
          :columns="sampleColumns"
          :data-source="sampleRows"
          :pagination="{ pageSize: 10, showSizeChanger: false }"
          :row-key="(record, index) => index"
          size="small"
          class="sample-table"
        />
      </template>

      <!-- ===== 적재 실행 ===== -->
      <div class="step-footer">
        <a-button
          type="primary"
          class="yellow-button"
          :disabled="(!previewResult.summary.endUsers && !previewResult.summary.orphanCustomers) || applying"
          @click="confirmVisible = true"
        >
          counsel-api 로 적재
        </a-button>
      </div>
    </template>

    <!-- ===== 적재 결과 ===== -->
    <template v-if="applyResult">
      <div class="section-title applied-title">적재 결과</div>
      <a-descriptions bordered size="small" :column="2" class="applied-desc">
        <a-descriptions-item label="EndUser">
          신규 {{ applyResult.applied.endUsers.created }} / 기존 {{ applyResult.applied.endUsers.existing }}
        </a-descriptions-item>
        <a-descriptions-item label="ChatRoom">
          신규 {{ applyResult.applied.rooms.created }} / 스킵 {{ applyResult.applied.rooms.skipped }}
        </a-descriptions-item>
        <a-descriptions-item label="Message">
          신규 {{ applyResult.applied.messages.created }} / 스킵 {{ applyResult.applied.messages.skipped }}
        </a-descriptions-item>
        <a-descriptions-item label="첨부">
          복구 {{ applyResult.applied.attachments.recovered }} / 만료
          {{ applyResult.applied.attachments.expired }} / 실패 {{ applyResult.applied.attachments.failed }}
        </a-descriptions-item>
        <!-- R7M-05: 청크 합산된 EndUser CDP push 수. cdpFailed 를 안 주는 옛 counsel-api 면 '-' -->
        <a-descriptions-item label="CDP 동기화">
          성공 {{ applyResult.applied.cdpSynced }} / 실패
          {{ applyResult.applied.cdpFailed ?? "-" }}
        </a-descriptions-item>
        <a-descriptions-item label="상담방 없는 고객 (CDP 직접)">
          성공 {{ applyResult.applied.orphanCustomers.synced }} / 실패
          {{ applyResult.applied.orphanCustomers.failed }}
        </a-descriptions-item>
      </a-descriptions>
    </template>

    <!-- 적재 실행 확인 모달 -->
    <a-modal :open="confirmVisible" :footer="null" @cancel="confirmVisible = false">
      <template #title>
        <div class="modal-title">상담·고객 적재 확인</div>
      </template>
      <div class="modal-content">
        <a-alert
          type="warning"
          show-icon
          message="counsel-api 에 고객/상담방/메시지가 대량 적재됩니다"
          description="드라이런에 쓴 파일을 그대로 다시 올려 서버가 적재합니다. DB unique 키로 중복을 건너뛰므로 여러 번 실행해도 안전합니다."
        />
        <ul class="confirm-list">
          <li>대상 병원: <strong>{{ companyName }}</strong></li>
          <li v-if="previewResult">
            EndUser: <strong>{{ previewResult.summary.endUsers.toLocaleString() }}명</strong>
          </li>
          <li v-if="previewResult">
            ChatRoom: <strong>{{ previewResult.summary.rooms.toLocaleString() }}개</strong>
          </li>
          <li v-if="previewResult">
            Message: <strong>{{ previewResult.summary.messages.toLocaleString() }}건</strong>
          </li>
          <li v-if="previewResult">
            태그 보유 고객: <strong>{{ previewResult.summary.tagged.toLocaleString() }}명</strong>
            <span class="muted">(태그는 원문 그대로 적재 — 시나리오 조인 키)</span>
          </li>
        </ul>
        <div class="modal-footer">
          <a-button @click="confirmVisible = false">취소</a-button>
          <a-button type="primary" class="yellow-button" :loading="applying" @click="runApply">
            적재 실행
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { message } from "ant-design-vue";
import { ExperimentOutlined } from "@ant-design/icons-vue";
import { counselMigrationApi, EXCLUDED_REASON_LABEL_MAP } from "../counselMigrationApi";
import { chunkCounselStore, mergeDeep } from "../afterdocCounselChunker";

// 청크당 고객 수. 한 요청이 게이트웨이 타임아웃 안에 끝나도록 작게 잡는다.
const CHUNK_SIZE = 100;

export default {
  name: "CounselMigrationPanel",
  components: { ExperimentOutlined },
  props: {
    // 상단 공용 선택기에서 내려주는 대상 병원
    companyId: { type: String, default: undefined },
    companyName: { type: String, default: "-" },
    // 상단에서 한 번 업로드한 data-store.json 원본 File (서버로 그대로 올린다)
    file: { type: Object, default: null },
    // detectTargets(store).counsel — 탐지된 규모 표시용
    detected: { type: Object, default: null },
  },
  setup(props) {
    const previewing = ref(false);
    const previewResult = ref(null);
    const confirmVisible = ref(false);
    const applying = ref(false);
    const applyResult = ref(null);
    // 청크 진행률: { current, total } (null 이면 진행 중 아님)
    const progress = ref(null);

    // 파일이나 병원이 바뀌면 이전 결과는 버린다 (다른 대상에 붙지 않도록)
    watch(
      () => [props.file, props.companyId],
      () => {
        previewResult.value = null;
        applyResult.value = null;
        progress.value = null;
      }
    );

    const resolveErrorMessage = (error, fallback) => {
      const data = error && error.response ? error.response.data : null;
      if (data && data.message) {
        return Array.isArray(data.message) ? data.message.join(", ") : data.message;
      }
      return fallback;
    };

    // data-store.json 을 브라우저에서 파싱해 고객 단위 부분 store 로 쪼갠다.
    // 각 부분을 작은 JSON 파일로 만들어 기존 파일 업로드 엔드포인트에 그대로 올린다(서버 무변경).
    const buildChunkFiles = async () => {
      const text = await props.file.text();
      const store = JSON.parse(text);
      const chunks = chunkCounselStore(store, CHUNK_SIZE);
      return chunks.map(
        (subset, i) =>
          new File([JSON.stringify(subset)], `counsel-chunk-${i + 1}.json`, {
            type: "application/json",
          })
      );
    };

    // 청크 파일들을 순차 호출하며 결과를 깊은 합산한다. 진행률을 갱신한다.
    const runChunked = async (call) => {
      const files = await buildChunkFiles();
      if (files.length === 0) {
        throw new Error("이관할 상담 데이터가 없습니다.");
      }
      progress.value = { current: 0, total: files.length };
      let merged = null;
      for (let i = 0; i < files.length; i++) {
        const res = await call(files[i]);
        merged = merged === null ? res.data : mergeDeep(merged, res.data);
        progress.value = { current: i + 1, total: files.length };
      }
      return merged;
    };

    const runPreview = async () => {
      if (!props.companyId) {
        message.warning("대상 병원을 먼저 선택해주세요.");
        return;
      }
      if (!props.file) {
        message.warning("data-store.json 을 먼저 업로드해주세요.");
        return;
      }

      previewing.value = true;
      applyResult.value = null;
      try {
        const data = await runChunked((file) =>
          counselMigrationApi.preview(props.companyId, file)
        );
        // 청크별 sample 을 이어붙였으므로 앞쪽 20건만 남긴다.
        if (data && Array.isArray(data.sample)) data.sample = data.sample.slice(0, 20);
        previewResult.value = data;
        message.success(
          `드라이런 완료. EndUser ${data.summary.endUsers}명, 상담방 ${data.summary.rooms}개, 메시지 ${data.summary.messages}건.`
        );
      } catch (error) {
        console.error("상담 드라이런 실패:", error);
        message.error(resolveErrorMessage(error, "드라이런 중 오류가 발생했습니다."));
      } finally {
        previewing.value = false;
        progress.value = null;
      }
    };

    const runApply = async () => {
      if (!props.companyId || !props.file) {
        message.warning("대상 병원과 파일을 먼저 확인해주세요.");
        return;
      }

      applying.value = true;
      try {
        const data = await runChunked((file) =>
          counselMigrationApi.apply(props.companyId, file)
        );
        applyResult.value = data;
        confirmVisible.value = false;
        message.success("상담·고객 적재가 완료되었습니다.");
      } catch (error) {
        console.error("상담 적재 실패:", error);
        message.error(resolveErrorMessage(error, "적재 중 오류가 발생했습니다."));
      } finally {
        applying.value = false;
        progress.value = null;
      }
    };

    const excludedRows = computed(() => {
      const excluded = previewResult.value && previewResult.value.summary.excluded;
      if (!excluded) return [];
      return Object.entries(excluded).map(([reason, count]) => ({
        reason: EXCLUDED_REASON_LABEL_MAP[reason] || reason,
        count,
      }));
    });

    // 서버가 sample[] 을 어떤 형태로 주든 표에 넣을 수 있게 최소 필드만 뽑는다
    const sampleRows = computed(() => (previewResult.value && previewResult.value.sample) || []);

    const excludedColumns = [
      { title: "제외 사유", dataIndex: "reason", key: "reason" },
      { title: "건수", dataIndex: "count", key: "count", width: 120, align: "right" },
    ];

    // counsel-api preview sample[] 실제 shape: { channel, channelUserId, name?, tags[], rooms, messages, existing }
    const sampleColumns = [
      { title: "채널", dataIndex: "channel", key: "channel", width: 110 },
      { title: "이름", dataIndex: "name", key: "name", width: 150, ellipsis: true },
      { title: "상담방", dataIndex: "rooms", key: "rooms", width: 80, align: "right" },
      { title: "메시지", dataIndex: "messages", key: "messages", width: 90, align: "right" },
      { title: "태그", dataIndex: "tags", key: "tags", customRender: ({ text }) => (Array.isArray(text) ? text.join(", ") : text) },
    ];

    return {
      previewing,
      previewResult,
      confirmVisible,
      applying,
      applyResult,
      progress,
      runPreview,
      runApply,
      excludedRows,
      sampleRows,
      excludedColumns,
      sampleColumns,
    };
  },
};
</script>

<style scoped>
.counsel-panel {
  padding-top: 4px;
}

.intro-alert {
  margin-bottom: 20px;
}

.need-company {
  margin-bottom: 20px;
}

.detected-line {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.action-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.stats-cards {
  margin-bottom: 16px;
}

.stats-cards .ant-card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.stat-sub {
  margin-top: 4px;
  font-size: 12px;
}

:deep(.ant-statistic-title) {
  font-size: 14px;
  color: #666;
}

:deep(.ant-statistic-content) {
  font-size: 24px;
  font-weight: 600;
}

.attach-note {
  margin-bottom: 24px;
}

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
}

.excluded-table,
.sample-table {
  margin-bottom: 24px;
}

.applied-title {
  margin-top: 24px;
}

.applied-desc {
  margin-bottom: 8px;
}

.step-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.muted {
  color: #999;
}

.confirm-list {
  margin: 16px 0 0;
  padding-left: 18px;
  line-height: 1.9;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  margin-top: 20px;
}

.yellow-button {
  background-color: #ffcc32;
  border-color: #ffcc32;
  color: #333;
}

.yellow-button:hover {
  background-color: #e6b82e;
  border-color: #e6b82e;
  color: #333;
}
</style>
